import { type DialogProps, styled } from "@material-ui/core";
import { DialogBody } from "./dialog-body";
import { DialogContainer } from "./dialog-container";
import { DialogFooter } from "./dialog-footer";
import { DialogHeader } from "./dialog-header";

type Props = {
  cancelLabel?: string;
  onCancel?: () => void;
  submitLabel?: string;
  onSubmit?: () => void;
  submitLoading?: boolean;
  submitDisabled?: boolean;
};

const Header = styled(DialogHeader)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const Footer = styled(DialogFooter)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

/**
 * A sugar component for creating a dialog with a header, body, and footer.
 * If you need more control over the dialog, use the `DialogContainer`, `DialogHeader`, `DialogBody`, and `DialogFooter` components directly.
 *
 * @param title The title of the dialog. If not supplied, the header will not show.
 * @param submitLabel The label for the submit button. If not supplied, the submit button will not show.
 * @param onSubmit The callback for the submit button.
 * @param cancelLabel The label for the cancel button. If not supplied, the cancel button will not show.
 * @param onCancel The callback for the cancel button. If not supplied, the cancel button will not show.
 *
 * All other props are passed to the root element.
 */
export const Dialog: FCwC<
  Omit<DialogProps, "children" | "onClose"> & Props
> = ({
  title,
  submitLabel,
  onSubmit,
  submitLoading,
  submitDisabled,
  cancelLabel,
  onCancel,
  children,
  ...rest
}) => {
  return (
    <DialogContainer onClose={onCancel} {...rest}>
      {title && <Header onClose={onCancel} title={title} />}
      <DialogBody>{children}</DialogBody>
      {((cancelLabel && onCancel) || (submitLabel && onSubmit)) && (
        <Footer
          submitLabel={submitLabel}
          onSubmit={onSubmit}
          submitLoading={submitLoading}
          submitDisabled={submitDisabled}
          cancelLabel={cancelLabel}
          onCancel={onCancel}
        />
      )}
    </DialogContainer>
  );
};
